import { useState } from 'react';

import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { StyledFlex, StyledMargin, UncontrolledCheckbox } from '@dreryk/react-components';

import { PrescriptionHistoryFilters } from '~libs.desktop.shared.patient-prescription-history';
import { useGetCurrentPractitionerGlobalState, useResourceBranchByHealthcareProviderType } from '~libs.shared.account';

import { patientTranslationKeys } from '../../translations';

import { PrescriptionsHistoryPractitionersFilter } from './PrescriptionsHistoryPractitionersFilter';
import { PrescriptionsHistoryFilterFormBean } from './PatientPrescriptionsHistoryFilters.types';

export type PatientPrescriptionsHistoryFiltersRenderProps = {
   readonly filters: PrescriptionHistoryFilters;
};

export type PatientPrescriptionsHistoryFiltersProps = {
   readonly doctors: DoctorSimple[];
   readonly children: (renderProps: PatientPrescriptionsHistoryFiltersRenderProps) => JSX.Element;
};

export const PatientPrescriptionsHistoryFilters = (props: PatientPrescriptionsHistoryFiltersProps): JSX.Element => {
   const currentPractitioner = useGetCurrentPractitionerGlobalState();
   const shouldShowOnlyMineFilter = useResourceBranchByHealthcareProviderType(true, false);
   const [continuouslyUsedMedicine, setContinuouslyUsedMedicine] = useState(false);

   const { register, watch, setValue } = useFormController<PrescriptionsHistoryFilterFormBean>({
      practitioners: [],
   });

   const selectedPractitioners = watch('practitioners');

   const onlyMine =
      selectedPractitioners.length === 1 && selectedPractitioners[0].practitionerId === currentPractitioner.id;

   const filters: PrescriptionHistoryFilters = {
      issuedByCurrentPractitioner: false,
      continuouslyUsedMedicine: continuouslyUsedMedicine,
      practitionerIds: selectedPractitioners.map((doctor) => doctor.practitionerId),
   };

   const handleOnlyMineChange = (value: boolean): void => {
      if (value) {
         const currentDoctor = props.doctors.find((doctor) => doctor.practitionerId === currentPractitioner.id);
         if (currentDoctor) {
            setValue('practitioners', [currentDoctor]);
         }
      } else {
         setValue('practitioners', []);
      }
   };

   const handleContinuouslyUsedMedicineChange = (value: boolean): void => {
      setContinuouslyUsedMedicine(value);
   };

   return (
      <>
         <StyledMargin bottom={8} top={1}>
            <StyledFlex gap={'16px'} alignItems={'center'}>
               {shouldShowOnlyMineFilter ? (
                  <UncontrolledCheckbox
                     testId={'issuedByCurrentPractitioner'}
                     name={'issuedByCurrentPractitioner'}
                     id={'issuedByCurrentPractitioner'}
                     label={patientTranslationKeys.prescriptionsHistoryFilters.onlyMine}
                     selected={onlyMine}
                     onSelectionChange={handleOnlyMineChange}
                  />
               ) : null}
               <UncontrolledCheckbox
                  testId={'continuouslyUsedMedicine'}
                  name={'continuouslyUsedMedicine'}
                  id={'continuouslyUsedMedicine'}
                  label={patientTranslationKeys.prescriptionsHistoryFilters.onlyContinuouslyUsed}
                  selected={continuouslyUsedMedicine}
                  onSelectionChange={handleContinuouslyUsedMedicineChange}
               />
               {props.doctors.length > 1 ? (
                  <PrescriptionsHistoryPractitionersFilter doctors={props.doctors} register={register} />
               ) : null}
            </StyledFlex>
         </StyledMargin>

         {props.children({ filters: filters })}
      </>
   );
};
